import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Row, Col, Card, Form, Input, Select, DatePicker, Radio, Checkbox, Button, Space,
  message, Typography, Spin, Modal,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, EditOutlined, CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PageWrapper from '../../components/common/PageWrapper';
import RichContentEditor from '../../components/editor/RichContentEditor';
import ObjectStorageUploader, { PORTAL_BUCKET } from '../../components/uploader/ObjectStorageUploader';
import PortalPostArticle from '../../components/portal/PortalPostArticle';
import {
  getPortalPostAdmin, createPortalPost, updatePortalPost,
  listPortalCategories, createPortalCategory, updatePortalCategory,
  listPortalTags, createPortalTag, updatePortalTag,
} from '../../services/portalApi';
import { PORTAL_POST_STATUS } from '../../utils/portalPostVisibility';

const { Text } = Typography;
const STATUS_OPTIONS = Object.values(PORTAL_POST_STATUS).map((s) => ({ value: s, label: s }));

const firstId = (json) => {
  try {
    const ids = JSON.parse(json || '[]');
    return ids[0] || null;
  } catch {
    return null;
  }
};

const TaxonomyEditor = ({
  items,
  mode,
  selectedNames,
  onToggleName,
  onAdd,
  onRename,
  addPlaceholder,
}) => {
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const submitAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await onAdd(name);
      setNewName('');
    } finally {
      setAdding(false);
    }
  };

  const submitRename = async (item) => {
    const name = editName.trim();
    if (!name || name === item.name) {
      setEditingId(null);
      return;
    }
    setSavingEdit(true);
    try {
      await onRename(item, name);
      setEditingId(null);
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {items.map((item) => (
          <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {editingId === item.id ? (
              <>
                <Input
                  size="large"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onPressEnter={() => submitRename(item)}
                />
                <Button size="large" type="primary" icon={<CheckOutlined />} loading={savingEdit} onClick={() => submitRename(item)} />
                <Button size="large" icon={<CloseOutlined />} onClick={() => setEditingId(null)} />
              </>
            ) : (
              <>
                {mode === 'radio' ? (
                  <Radio value={item.id} style={{ flex: 1 }}>{item.name}</Radio>
                ) : (
                  <Checkbox
                    checked={selectedNames?.includes(item.name)}
                    onChange={() => onToggleName(item.name)}
                    style={{ flex: 1 }}
                  >
                    {item.name}
                  </Checkbox>
                )}
                <Button
                  type="text"
                  size="large"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditingId(item.id);
                    setEditName(item.name);
                  }}
                />
              </>
            )}
          </div>
        ))}
      </Space>
      <div style={{ display: 'flex', gap: 8, marginTop: items.length ? 16 : 0 }}>
        <Input
          size="large"
          placeholder={addPlaceholder}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={submitAdd}
        />
        <Button size="large" icon={<PlusOutlined />} loading={adding} onClick={submitAdd}>
          Add
        </Button>
      </div>
    </>
  );
};

const PortalPostFormScreen = () => {
  const { id } = useParams();
  const isCreate = !id;
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const status = Form.useWatch('status', form);
  const tagNames = Form.useWatch('tagNames', form) || [];
  const userInfo = useSelector((s) => s.userLogin?.userInfo);

  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [attachmentsJson, setAttachmentsJson] = useState('[]');
  const [featuredJson, setFeaturedJson] = useState('[]');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPost, setPreviewPost] = useState(null);

  const loadCategories = () => listPortalCategories().then((d) => setCategories(d.items || [])).catch(() => {});
  const loadTags = () => listPortalTags().then((d) => setTags(d.items || [])).catch(() => {});

  useEffect(() => {
    loadCategories();
    loadTags();
  }, []);

  useEffect(() => {
    if (isCreate) {
      form.setFieldsValue({ status: PORTAL_POST_STATUS.Draft, tagNames: [] });
      return;
    }
    setLoading(true);
    getPortalPostAdmin(id)
      .then((record) => {
        setEditing(record);
        form.setFieldsValue({
          ...record,
          tagNames: record.tagNames || [],
          scheduledAt: record.scheduledAt ? dayjs(record.scheduledAt) : null,
          password: undefined,
        });
        setAttachmentsJson(JSON.stringify(record.attachmentObjectIds || []));
        setFeaturedJson(record.featuredMediaId ? JSON.stringify([record.featuredMediaId]) : '[]');
      })
      .catch(() => message.error('Failed to load post'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleAddCategory = async (name) => {
    const created = await createPortalCategory({ name });
    await loadCategories();
    form.setFieldsValue({ categoryId: created.id });
  };

  const handleRenameCategory = async (item, name) => {
    await updatePortalCategory(item.id, { ...item, name });
    await loadCategories();
  };

  const handleAddTag = async (name) => {
    const created = await createPortalTag({ name });
    await loadTags();
    const current = form.getFieldValue('tagNames') || [];
    if (!current.includes(created.name)) {
      form.setFieldsValue({ tagNames: [...current, created.name] });
    }
  };

  const handleRenameTag = async (item, name) => {
    await updatePortalTag(item.id, { ...item, name });
    const current = form.getFieldValue('tagNames') || [];
    form.setFieldsValue({
      tagNames: current.map((n) => (n === item.name ? name : n)),
    });
    await loadTags();
  };

  const parseIds = (json) => {
    try {
      const ids = JSON.parse(json || '[]');
      return Array.isArray(ids) ? ids : [];
    } catch {
      return [];
    }
  };

  const loadStorageObjects = async (ids) => {
    if (!ids.length || !userInfo?.token) return [];
    const { data } = await axios.post(
      `/dotnet_api/v1/storage/objects/${PORTAL_BUCKET}`,
      ids,
      { headers: { Authorization: `Bearer ${userInfo.token}` } },
    );
    return data || [];
  };

  const openPreview = async () => {
    try {
      const fields = ['title', 'bodyHtml'];
      if (status === PORTAL_POST_STATUS.PasswordProtected) fields.push('password');
      if (status === PORTAL_POST_STATUS.Scheduled) fields.push('scheduledAt');
      await form.validateFields(fields);
    } catch {
      return;
    }

    const values = form.getFieldsValue(true);
    const category = categories.find((c) => c.id === values.categoryId);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const featuredId = firstId(featuredJson);
      const attachmentIds = parseIds(attachmentsJson);
      const [featuredObjs, attachmentObjs] = await Promise.all([
        featuredId ? loadStorageObjects([featuredId]) : Promise.resolve([]),
        loadStorageObjects(attachmentIds),
      ]);
      setPreviewPost({
        title: values.title,
        bodyHtml: values.bodyHtml,
        excerpt: values.excerpt,
        categoryName: category?.name,
        tagNames: values.tagNames || [],
        authorName: editing?.authorName || userInfo?.userName,
        publishedAt: editing?.publishedAt || new Date().toISOString(),
        needsPassword: values.status === PORTAL_POST_STATUS.PasswordProtected,
        featuredMediaUrl: featuredObjs[0]?.url,
        attachments: attachmentObjs.map((o) => ({
          name: o.name,
          url: o.url,
          size: o.size,
        })),
      });
    } catch {
      setPreviewPost({
        title: values.title,
        bodyHtml: values.bodyHtml,
        categoryName: category?.name,
        tagNames: values.tagNames || [],
        authorName: editing?.authorName || userInfo?.userName,
        publishedAt: editing?.publishedAt || new Date().toISOString(),
        needsPassword: values.status === PORTAL_POST_STATUS.PasswordProtected,
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const onSave = async (values) => {
    const payload = {
      ...values,
      scheduledAt: values.scheduledAt ? values.scheduledAt.toISOString() : null,
      attachmentObjectIds: (() => {
        try {
          return JSON.parse(attachmentsJson);
        } catch {
          return [];
        }
      })(),
      featuredMediaId: firstId(featuredJson),
    };
    setSaving(true);
    try {
      if (editing) {
        await updatePortalPost(editing.id, { ...editing, ...payload, password: values.password || undefined });
        message.success('Post updated');
      } else {
        await createPortalPost(payload);
        message.success('Post created');
      }
      navigate('/settings/portal_posts');
    } catch (e) {
      message.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <PageWrapper title={isCreate ? 'New post' : 'Edit post'} icon={null}>
      <Button
        type="link"
        size="large"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/settings/portal_posts')}
        style={{ marginBottom: 12, paddingLeft: 0 }}
      >
        Back to posts
      </Button>
      <Form form={form} layout="vertical" size="large" onFinish={onSave}>
        <Row gutter={24}>
          <Col xs={24} lg={16}>
            <Card style={{ marginBottom: 16 }}>
              <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                <Input placeholder="Post title" />
              </Form.Item>
              <Form.Item name="slug" label="Slug">
                <Input placeholder="Generated from title if empty" />
              </Form.Item>
              <Form.Item name="excerpt" label="Excerpt">
                <Input.TextArea rows={2} />
              </Form.Item>
              <Form.Item name="bodyHtml" label="Content" rules={[{ required: true, message: 'Content is required' }]}>
                <RichContentEditor showMetadata={false} height="420px" />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card title="Publish" style={{ marginBottom: 16 }}>
              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                <Select options={STATUS_OPTIONS} />
              </Form.Item>
              {status === PORTAL_POST_STATUS.Scheduled && (
                <Form.Item name="scheduledAt" label="Publish at" rules={[{ required: true }]}>
                  <DatePicker showTime style={{ width: '100%' }} />
                </Form.Item>
              )}
              {status === PORTAL_POST_STATUS.PasswordProtected && (
                <Form.Item
                  name="password"
                  label="Password"
                  extra={editing?.hasPassword ? 'Leave blank to keep the current password' : null}
                  rules={editing?.hasPassword ? [] : [{ required: true, message: 'Password is required' }]}
                >
                  <Input.Password />
                </Form.Item>
              )}
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                <Button block size="large" icon={<EyeOutlined />} onClick={openPreview}>
                  Preview
                </Button>
                <Button type="primary" block size="large" loading={saving} onClick={() => form.submit()}>
                  {isCreate ? 'Publish' : 'Update'}
                </Button>
              </Space>
            </Card>

            <Card title="Category" style={{ marginBottom: 16 }}>
              <Form.Item name="categoryId" noStyle>
                <Radio.Group style={{ width: '100%' }}>
                  <TaxonomyEditor
                    items={categories}
                    mode="radio"
                    onAdd={async (name) => {
                      try {
                        await handleAddCategory(name);
                      } catch (e) {
                        message.error(e.response?.data?.detail || 'Failed to add category');
                      }
                    }}
                    onRename={async (item, name) => {
                      try {
                        await handleRenameCategory(item, name);
                      } catch (e) {
                        message.error(e.response?.data?.detail || 'Failed to update category');
                      }
                    }}
                    addPlaceholder="Add new category"
                  />
                </Radio.Group>
              </Form.Item>
            </Card>

            <Card title="Tags" style={{ marginBottom: 16 }}>
              <Form.Item name="tagNames" hidden>
                <Select mode="multiple" options={tags.map((t) => ({ value: t.name, label: t.name }))} />
              </Form.Item>
              <TaxonomyEditor
                items={tags}
                mode="checkbox"
                selectedNames={tagNames}
                onToggleName={(name) => {
                  const current = form.getFieldValue('tagNames') || [];
                  form.setFieldsValue({
                    tagNames: current.includes(name)
                      ? current.filter((n) => n !== name)
                      : [...current, name],
                  });
                }}
                onAdd={async (name) => {
                  try {
                    await handleAddTag(name);
                  } catch (e) {
                    message.error(e.response?.data?.detail || 'Failed to add tag');
                  }
                }}
                onRename={async (item, name) => {
                  try {
                    await handleRenameTag(item, name);
                  } catch (e) {
                    message.error(e.response?.data?.detail || 'Failed to update tag');
                  }
                }}
                addPlaceholder="Add new tag"
              />
            </Card>

            <Card title="Featured image" style={{ marginBottom: 16 }}>
              <ObjectStorageUploader
                value={featuredJson}
                onChange={(v) => setFeaturedJson(JSON.stringify(firstId(v) ? [firstId(v)] : []))}
                category="featured"
                ownerId={userInfo?.id || ''}
                ownerName={userInfo?.userName || ''}
              />
            </Card>

            <Card title="Attachments">
              <ObjectStorageUploader
                value={attachmentsJson}
                onChange={setAttachmentsJson}
                category="posts"
                ownerId={userInfo?.id || ''}
                ownerName={userInfo?.userName || ''}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Images pasted into the editor are stored inline. Files here are offered as downloadable attachments.
              </Text>
            </Card>
          </Col>
        </Row>
      </Form>
      <Modal
        title="Post preview"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        width={840}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
        destroyOnClose
        footer={[
          <Button key="close" size="large" onClick={() => setPreviewOpen(false)}>Close</Button>,
          <Button
            key="publish"
            type="primary"
            size="large"
            loading={saving}
            onClick={async () => {
              form.setFieldsValue({ status: PORTAL_POST_STATUS.Published });
              try {
                const values = await form.validateFields();
                await onSave({ ...values, status: PORTAL_POST_STATUS.Published });
              } catch {
                setPreviewOpen(false);
              }
            }}
          >
            Publish
          </Button>,
        ]}
      >
        {previewLoading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : (
          <PortalPostArticle post={previewPost} compact />
        )}
      </Modal>
    </PageWrapper>
  );
};

export default PortalPostFormScreen;
